import React, { useEffect, useState, useRef } from 'react';
import { Church, Plus, Search, Users, Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Shield } from 'lucide-react';
import { MinistriesService, type Ministerio } from '../../services/ministries/ministries.service';
import { CreateMinisterioDrawer } from '../../components/ministries/CreateMinisterioDrawer';
import { MinisterioDetailsDrawer } from '../../components/ministries/MinisterioDetailsDrawer';
import { MinisteriosStats } from '../../components/ministries/MinisteriosStats';
import { NotificationToast } from '../../components/ui/NotificationToast';
import { useAuthStore } from '../../store/auth.store';
import { usePermissions } from '../../hooks/usePermissions';

export const MinisteriosPage = () => {
  const { user: authUser } = useAuthStore();
  const { hasPermission } = usePermissions();

  // Permisos del módulo Ministerios
  const canAdminister = hasPermission('Ministerios', 'administrar');
  const canCreate = hasPermission('Ministerios', 'crear');
  const canEdit = hasPermission('Ministerios', 'editar');
  const canDelete = hasPermission('Ministerios', 'eliminar');
  const canToggle = hasPermission('Ministerios', 'activar') || hasPermission('Ministerios', 'desactivar');
  // Ver todos vs. ver solo los propios
  const canViewAll = canAdminister;

  const [ministerios, setMinisterios] = useState<Ministerio[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [total, setTotal] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingMinisterio, setEditingMinisterio] = useState<Ministerio | null>(null);
  const [selectedMinisterio, setSelectedMinisterio] = useState<Ministerio | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipSearchEffect = useRef(true);
  const skipPageLimitEffect = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);

  const [meta, setMeta] = useState<{ page: number; limit: number; total: number; totalPages: number; hasPrevious: boolean; hasNext: boolean } | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'stats'>('list');
  const [allMinisterios, setAllMinisterios] = useState<Ministerio[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);

  const loadMinisterios = async (currentPage = page, currentLimit = limit, currentSearch = search) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);
    try {
      // Usuarios con permiso 'administrar' ven todos los ministerios
      if (canViewAll) {
        const result = await MinistriesService.findAll(currentPage, currentLimit, currentSearch);
        setMinisterios(result.data);
        setTotal(result.total);
        const totalPages = Math.ceil(result.total / currentLimit);
        setMeta({
          page: result.page,
          limit: result.limit,
          total: result.total,
          totalPages,
          hasPrevious: result.page > 1,
          hasNext: result.page < totalPages,
        });
        return;
      }

      // No administradores: solo los ministerios donde participan (server-side)
      const result = await MinistriesService.findAll(currentPage, currentLimit, '', true);
      setMinisterios(result.data);
      setTotal(result.total);
      const totalPages = Math.max(1, Math.ceil(result.total / currentLimit));
      setMeta({
        page: result.page,
        limit: result.limit,
        total: result.total,
        totalPages,
        hasPrevious: result.page > 1,
        hasNext: result.page < totalPages,
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : 'Error al cargar ministerios';
      setError(msg);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  };

  const loadAllMinisteriosForStats = async () => {
    if (!canAdminister) return; // Solo administradores ven estadísticas
    setLoadingStats(true);
    try {
      const result = await MinistriesService.findAll(1, 1000, '');
      setAllMinisterios(result.data);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  // Carga inicial única al montar el componente
  useEffect(() => {
    loadMinisterios(page, limit, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipSearchEffect.current) {
      skipSearchEffect.current = false;
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      loadMinisterios(1, limit, search);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (skipPageLimitEffect.current) {
      skipPageLimitEffect.current = false;
      return;
    }
    loadMinisterios(page, limit, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  // Cargar todos los ministerios cuando se active la pestaña de estadísticas
  useEffect(() => {
    if (activeTab === 'stats' && allMinisterios.length === 0) {
      loadAllMinisteriosForStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const showToast = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setNotificationConfig({ title, message, type });
    setShowNotification(true);
  };

  const handleDelete = async (id: string, nombre: string) => {
    if (!window.confirm(`¿Estás seguro de eliminar el ministerio "${nombre}"?`)) return;
    try {
      await MinistriesService.delete(id);
      showToast('Ministerio eliminado', `"${nombre}" fue eliminado exitosamente.`, 'success');
      await loadMinisterios(page, limit, search);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al eliminar ministerio';
      showToast('Error', msg, 'error');
    }
  };

  const handleToggleStatus = async (m: Ministerio) => {
    try {
      await MinistriesService.toggleStatus(m.id);
      showToast(
        'Estado actualizado',
        `El ministerio "${m.nombre}" fue ${m.estado ? 'desactivado' : 'activado'}.`,
        'success'
      );
      await loadMinisterios(page, limit, search);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cambiar el estado';
      showToast('Error', msg, 'error');
    }
  };

  const handleEdit = (m: Ministerio) => {
    setEditingMinisterio(m);
    setIsCreateOpen(true);
  };

  const handleOpenCreate = () => {
    setEditingMinisterio(null);
    setIsCreateOpen(true);
  };

  const handleViewDetail = (m: Ministerio) => {
    setSelectedMinisterio(m);
    setIsDetailOpen(true);
  };

  const handleEditFromDetail = (m: Ministerio) => {
    // No cerramos el detalle, el drawer de editar se abre sobre él
    setEditingMinisterio(m);
    setIsCreateOpen(true);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    setIsDropdownOpen(false);
  };

  const totalPages = meta?.totalPages ?? Math.ceil(total / limit);
  const hasPrevious = meta?.hasPrevious ?? page > 1;
  const hasNext = meta?.hasNext ?? page < totalPages;

  return (
    <>
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
        />
      )}

      <CreateMinisterioDrawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={async () => {
          await loadMinisterios(page, limit, search);
          // Si estábamos editando desde el detalle, refrescar el ministerio seleccionado
          if (selectedMinisterio && editingMinisterio?.id === selectedMinisterio.id) {
            try {
              const updated = await MinistriesService.getById(selectedMinisterio.id);
              setSelectedMinisterio(updated);
            } catch { /* ignore */ }
          }
        }}
        ministerio={editingMinisterio}
      />

      <div className="relative h-full">
        <MinisterioDetailsDrawer
          isOpen={isDetailOpen}
          ministerio={selectedMinisterio}
          onClose={() => setIsDetailOpen(false)}
          onEdit={canEdit ? handleEditFromDetail : undefined}
          onToggleStatus={canToggle ? handleToggleStatus : undefined}
          onDelete={canDelete ? handleDelete : undefined}
          onRefresh={() => loadMinisterios(page, limit, search)}
        />

        <div className="p-4 lg:p-6">
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
            <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200/50 rounded-lg px-2.5 sm:px-4 py-1.5 sm:py-2">
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                  <div className="text-xs sm:text-xs">
                    <span className="font-medium text-purple-700">{total}</span>
                    <span className="hidden sm:inline text-gray-600 ml-1">ministerio{total !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
              {activeTab === 'list' && (
                <>
                  {canViewAll && (
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar..."
                        className="pl-9 pr-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 w-full sm:w-64 text-xs"
                      />
                    </div>
                  )}
                  {canCreate && (
                    <button
                      onClick={handleOpenCreate}
                      className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
                    >
                      <Plus className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline text-xs">Crear Ministerio</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Pestañas */}
          <div className="flex space-x-4 md:space-x-8 mb-6 border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('list')}
              className={`py-2 px-1 text-xs sm:text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'list'
                  ? 'text-purple-700 border-purple-700'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {canViewAll ? 'Lista de Ministerios' : 'Mi Ministerio'}
            </button>
            {canAdminister && (
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-2 px-1 text-xs sm:text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'stats'
                    ? 'text-purple-700 border-purple-700'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Dashboard Estadístico
              </button>
            )}
          </div>

          {canAdminister && activeTab === 'stats' && (
            <MinisteriosStats
              ministerios={allMinisterios}
              isLoading={loadingStats}
            />
          )}

          {activeTab === 'list' && loading && (
            <div className="flex items-center justify-center py-12">
              <svg className="animate-spin h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="ml-3 text-gray-600">Cargando ministerios...</span>
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 mb-6">
              {error}
            </div>
          )}

          {activeTab === 'list' && !loading && !error && ministerios.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-purple-100 flex items-center justify-center mb-6">
                <Church className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-xs font-medium text-gray-900 mb-2">
                {canViewAll ? 'No se encontraron ministerios' : 'No tienes ministerios asignados'}
              </h2>
              <p className="text-gray-500 max-w-md mx-auto">
                {canViewAll
                  ? (search ? 'Intenta con otro término de búsqueda.' : 'Comienza creando el primer ministerio.')
                  : 'Contacta a un administrador para que te asigne a un ministerio.'}
              </p>
            </div>
          )}

          {activeTab === 'list' && !loading && !error && ministerios.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {ministerios.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => handleViewDetail(m)}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 flex flex-col hover:shadow-md hover:border-purple-300 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h3 className="text-xs sm:text-xs font-medium text-gray-900 line-clamp-1" title={m.nombre}>
                        {m.nombre}
                      </h3>
                      <span
                        className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                          m.estado ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {m.estado ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>

                    <p className="text-xs sm:text-xs text-gray-600 line-clamp-2 mb-3 sm:mb-4 flex-1">
                      {m.descripcion || 'Sin descripción'}
                    </p>

                    <div className="text-xs sm:text-xs text-gray-700 mb-3 sm:mb-4">
                      <span className="text-gray-500">Líder:</span>{' '}
                      {m.lider ? `${m.lider.nombres} ${m.lider.apellidos}` : 'Sin líder'}
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-xs text-gray-600">
                      <div className="flex items-center" title="Integrantes">
                        <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 text-purple-600" />
                        {m.integrantes?.length || 0}
                      </div>
                      <div
                        className={`flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                          (m.eventos?.length || 0) > 0
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                        title="Eventos"
                      >
                        <Calendar className={`w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 ${(m.eventos?.length || 0) > 0 ? 'text-orange-600' : 'text-gray-500'}`} />
                        {m.eventos?.length || 0} evento{(m.eventos?.length || 0) !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="px-4 lg:px-6 py-4 lg:py-6 border-t border-gray-100 bg-gray-50/30 mt-6 sm:mt-8 rounded-b-xl">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    {/* Información de resultados */}
                    <div className="flex items-center space-x-4 md:space-x-6">
                      <div className="text-xs md:text-xs text-gray-600">
                        <span className="font-medium text-gray-900">
                          {((page - 1) * limit) + 1}
                        </span>
                        <span className="mx-1">–</span>
                        <span className="font-medium text-gray-900">
                          {Math.min(page * limit, total)}
                        </span>
                        <span className="mx-1 hidden sm:inline">de</span>
                        <span className="hidden sm:inline font-medium text-gray-900">{total}</span>
                        <span className="ml-1 hidden sm:inline">resultados</span>
                      </div>
                      <div className="hidden md:block text-xs text-gray-500">
                        Página <span className="font-medium text-gray-900">{page}</span> de <span className="font-medium text-gray-900">{totalPages}</span>
                      </div>
                    </div>

                    {/* Selector de cantidad por página */}
                    <div className="flex items-center space-x-1.5 sm:space-x-2">
                      <span className="hidden sm:inline text-xs text-gray-500 font-medium">Mostrar</span>
                      <div className="relative" data-dropdown>
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="bg-white border border-gray-300 rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 pr-7 sm:pr-8 text-xs sm:text-xs font-medium text-gray-700 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all cursor-pointer flex items-center min-w-[3rem]"
                        >
                          {limit}
                        </button>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 sm:pr-2 pointer-events-none">
                          <svg className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        {isDropdownOpen && (
                          <div className="absolute bottom-full left-0 mb-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                            {[8, 12, 16, 24, 48].map((value) => (
                              <div
                                key={value}
                                onClick={() => handleLimitChange(value)}
                                className="px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                              >
                                {value}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Controles de paginación */}
                    <div className="flex items-center space-x-1">
                      {/* Botón primera página */}
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={!hasPrevious}
                        className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-lg transition-all ${
                          hasPrevious
                            ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                        title="Primera página"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </button>

                      {/* Botón página anterior */}
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={!hasPrevious}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                          hasPrevious
                            ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                        title="Página anterior"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      {/* Páginas centrales */}
                      <div className="flex items-center space-x-1 mx-1 md:mx-2">
                        {(() => {
                          const pages = [];
                          const maxVisiblePages = typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 3;
                          let start = Math.max(1, page - Math.floor(maxVisiblePages / 2));
                          let end = Math.min(totalPages, start + maxVisiblePages - 1);

                          start = Math.max(1, end - maxVisiblePages + 1);

                          if (start > 1) {
                            pages.push(
                              <button
                                key="1"
                                onClick={() => handlePageChange(1)}
                                className="hidden sm:flex w-9 h-9 items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                              >
                                1
                              </button>
                            );
                            if (start > 2) {
                              pages.push(
                                <span key="ellipsis-start" className="hidden sm:flex w-9 h-9 items-center justify-center text-gray-400">⋯</span>
                              );
                            }
                          }

                          for (let i = start; i <= end; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                className={`w-9 h-9 flex items-center justify-center text-xs font-medium rounded-lg transition-all ${
                                  i === page
                                    ? 'bg-purple-600 text-white shadow-sm'
                                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }

                          if (end < totalPages) {
                            if (end < totalPages - 1) {
                              pages.push(
                                <span key="ellipsis-end" className="hidden sm:flex w-9 h-9 items-center justify-center text-gray-400">⋯</span>
                              );
                            }
                            pages.push(
                              <button
                                key={totalPages}
                                onClick={() => handlePageChange(totalPages)}
                                className="hidden sm:flex w-9 h-9 items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                              >
                                {totalPages}
                              </button>
                            );
                          }

                          return pages;
                        })()}
                      </div>

                      {/* Botón página siguiente */}
                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={!hasNext}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                          hasNext
                            ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                        title="Página siguiente"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>

                      {/* Botón última página */}
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={!hasNext}
                        className={`hidden sm:flex w-9 h-9 items-center justify-center rounded-lg transition-all ${
                          hasNext
                            ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                        title="Última página"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MinisteriosPage;
